import { MDSpecialContext } from "@/zapi-frontend/utils/md-special-context";

export type TDiffOperation = {
  type: "insert" | "delete" | "equal";
  text: string;
};

const TABLE_ROW_RE = /^(?!\s{0,3}(?:`{3,}|~{3,}))\s*\|?[^|\n]*\|[^|\n]*(?:\|[^|\n]*)*\|?\s*$/;

const TABLE_SEPARATOR_RE = /^(?!\s{0,3}(?:`{3,}|~{3,}))\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/;

function isHtmlTableRow(text: string): boolean {
  return (
    /<tr[\s>]/i.test(text) ||
    /<\/tr>/i.test(text) ||
    /<th[\s>]/i.test(text) ||
    /<td[\s>]/i.test(text) ||
    /<\/th>/i.test(text) ||
    /<\/td>/i.test(text)
  );
}

function isHtmlTable(text: string): boolean {
  const trimmed = text.trim();
  return /<table[\s>]/i.test(trimmed) && /<\/table>/i.test(trimmed);
}

export const isTableRow = (text: string) =>
  TABLE_ROW_RE.test(text) || TABLE_SEPARATOR_RE.test(text) || isHtmlTableRow(text);

const MDSpecialContextsToCheck = [
  new MDSpecialContext("**", "**", "bold"),
  new MDSpecialContext("*", "*", "italic", {
    banCharsAfterStart: "* ",
    banCharsBeforeEnd: " *",
    banCharsBeforeStart: "*",
    banCharsAfterEnd: "*"
  }),
  new MDSpecialContext("[", "]", "square-brackets")
];

function countWhitespace(text: string): number {
  const whitespaceChars = [" ", "\n", "\t", "\r"];
  return text.split("").filter((char) => whitespaceChars.includes(char)).length;
}

function removeWhitespace(text: string): string {
  const whitespaceChars = [" ", "\n", "\t", "\r", "\\"];
  return text
    .split("")
    .filter((char) => !whitespaceChars.includes(char))
    .join("");
}

function decodeHtmlEntities(text: string): string {
  const entityMap: { [key: string]: string } = {
    "&gt;": ">",
    "&lt;": "<",
    "&amp;": "&",
    "&quot;": '"',
    "&apos;": "'",
    "&#39;": "'",
    "&nbsp;": " ",
    "&mdash;": "—",
    "&ndash;": "–",
    "&ldquo;": '"',
    "&rdquo;": '"',
    "&lsquo;": "'",
    "&rsquo;": "'",
    "&hellip;": "…"
  };

  return text.replace(/&[a-zA-Z0-9#]+;/g, (match) => {
    return entityMap[match] || match;
  });
}

function isEndOfSentence(text: string, index: number, currentSentence: string): boolean {
  const sentenceSeparators = [".", "!", "?", "*", "\n"];
  if (index === text.length - 1) {
    return true;
  }
  if (sentenceSeparators.includes(text[index])) {
    if (text[index] === "\n") {
      return true;
    }

    if (currentSentence.length > 15 || text[index] !== ".") {
      if (text[index + 1] === " " || text[index + 1] === "\n") {
        return true;
      }
    }
  }
  return false;
}

function isMarkdownTable(block: string): boolean {
  if (isHtmlTable(block)) {
    return true;
  }

  const lines = block.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length < 2) return false;

  return lines.every((line) => isTableRow(line));
}

export function splitIntoSentences(text: string): string[] {
  const sentences: string[] = [];
  let currentSentence = "";

  const blocks = text.split("\n\n");

  while (blocks.length > 0 && countWhitespace(blocks[blocks.length - 1]) === blocks[blocks.length - 1].length) {
    blocks.pop();
  }
  let lastElementWasEndOfSentence = false;
  blocks.forEach((block, indexBlock) => {
    if (isMarkdownTable(block)) {
      if (currentSentence.trim()) {
        sentences.push(currentSentence);
        currentSentence = "";
      }

      sentences.push(block + (indexBlock !== blocks.length - 1 ? "\n\n" : ""));
      return;
    }

    const specialContextsStack: MDSpecialContext[] = [];
    for (let indexChar = 0; indexChar < block.length; indexChar++) {
      const char = block[indexChar];
      currentSentence += char;

      let usedAsSpecialContext = false;

      for (let i = specialContextsStack.length - 1; i >= 0; i--) {
        const context = specialContextsStack[i];
        if (context.isEndOfContext(block, indexChar)) {
          specialContextsStack.pop();
          usedAsSpecialContext = true;
          break;
        }
      }

      if (!usedAsSpecialContext) {
        for (const context of MDSpecialContextsToCheck) {
          if (context.isStartOfContext(block, indexChar)) {
            specialContextsStack.push(context);
          }
        }
      }

      if (lastElementWasEndOfSentence) {
        lastElementWasEndOfSentence = false;
        continue;
      }

      const insideSquareBrackets = specialContextsStack.some((c) => c.name === "square-brackets");
      if (insideSquareBrackets && char !== "\n") {
        continue;
      }

      if (isEndOfSentence(block, indexChar, currentSentence)) {
        for (let i = specialContextsStack.length - 1; i >= 0; i--) {
          const context = specialContextsStack[i];
          currentSentence += context.end;
        }

        if (block[indexChar] !== "\n" && indexChar + 1 < block.length) {
          currentSentence += block[indexChar + 1];
          indexChar++;
        }
        sentences.push(currentSentence);
        lastElementWasEndOfSentence = true;
        currentSentence = "";
        for (let i = specialContextsStack.length - 1; i >= 0; i--) {
          const context = specialContextsStack[i];
          currentSentence += context.start;
        }
      }
    }

    if (currentSentence) {
      sentences.push(currentSentence);
      currentSentence = "";
    }
    if (indexBlock !== blocks.length - 1) {
      sentences[sentences.length - 1] = sentences[sentences.length - 1] + "\n\n";
    }
  });
  while (
    sentences.length > 0 &&
    countWhitespace(sentences[sentences.length - 1]) === sentences[sentences.length - 1].length
  ) {
    sentences.pop();
  }
  return sentences;
}

function normalizeSasUrls(text: string): string {
  const urlPattern = /https?:\/\/[^\s)]+/g;

  return text.replace(urlPattern, (url) => {
    try {
      const urlObj = new URL(url);
      const params = urlObj.searchParams;

      const sasParamsToRemove = ["st", "se", "sig", "sv"];
      sasParamsToRemove.forEach((param) => params.delete(param));

      urlObj.search = params.toString();
      return urlObj.toString();
    } catch {
      return url;
    }
  });
}

// This has to do with AI sometimes using - instead of * for list markers
function normalizeListMarkers(text: string): string {
  // Convert - to * at the beginning of lines (with optional leading whitespace)
  return text.replace(/^(\s*)-(\s)/gm, "$1*$2");
}

function areTheSame(text1: string, text2: string): boolean {
  const normalized1 = normalizeSasUrls(removeWhitespace(decodeHtmlEntities(normalizeListMarkers(text1))));
  const normalized2 = normalizeSasUrls(removeWhitespace(decodeHtmlEntities(normalizeListMarkers(text2))));
  return normalized1 === normalized2;
}

export function lcsDiff(oldText: string, newText: string): TDiffOperation[] {
  if (oldText === newText) {
    return oldText ? [{ type: "equal", text: oldText }] : [];
  }

  const oldLines = splitIntoSentences(oldText);
  const newLines = splitIntoSentences(newText);

  const m = oldLines.length;
  const n = newLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (areTheSame(oldLines[i], newLines[j])) {
        dp[i][j] = 1 + dp[i + 1][j + 1];
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const result: TDiffOperation[] = [];
  let i = 0,
    j = 0;
  while (i < m && j < n) {
    if (areTheSame(oldLines[i], newLines[j])) {
      result.push({ type: "equal", text: newLines[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ type: "delete", text: oldLines[i++] });
    } else {
      result.push({ type: "insert", text: newLines[j++] });
    }
  }

  while (i < m) result.push({ type: "delete", text: oldLines[i++] });
  while (j < n) result.push({ type: "insert", text: newLines[j++] });
  return result;
}
